import { Injectable } from '@angular/core';
import { ConfigService } from '../services/config.service';

/** 
 * Adds additional properties and functions to the searchInput fieldset used throughtout the app.
 */
@Injectable({
	providedIn: 'root'
})
export class FieldsService {

	constructor(
		private configService: ConfigService
	) { }

	decorateUserSearchInputs(searchInputs: any[]): void {
		// Autosuggest decoration
		let suggestFields = this.configService.getConfig().search.autoSuggest.enabled ? this.configService.getConfig().search.autoSuggest.fields : []
		for (let idx = 0; idx < suggestFields.length; idx++) {
			let input = searchInputs.filter(function(x) {
				return x.name == suggestFields[idx];
			})[0];
			if (input) input.suggest = true;
		}

		// UI input decoration
		let uiDecorations = this.configService.getConfig().advancedSearchPanel.searchCriteria.fieldAttributes || [];
		for (let idx = 0; idx < uiDecorations.length; idx++) {
			let decor = uiDecorations[idx];
			let input = searchInputs.filter(function(x) {
				return x.name == decor.field;
			})[0];
			if (input) {
				input.placeholder = decor.placeholder;
				input.tooltip = decor.tooltip;
			}
		}

		// Additional properties decoration
		for (let field of searchInputs) {
			field.draggable = this.configService.getConfig().ui?.drag || false;
			
			field.type_normalized = field?.type.toUpperCase() || '';
			field.placeholder = field.placeholder || '';
			field.tooltip = field.tooltip || '';
			
			
			// Reconcile profile field name and schema name (for edit / upload template processing)
			// In some cases, fields named one way in the edit form are named differently in the user's profile field list.  
			// For example: 'description' in the metadata is indexed as 'db_description'. 
			// The edit form references the metadata name.
			if (field.name === 'db_description') {
				field.form_name = 'description'
			}			
			
			
			this.filterOptions(field);
			
			// Used for dependent field options filtering
			field.updateFn = this.onUpdateFn.bind(this);
		}
	}

	
	/**
	 * Creates an options list on the field separate from the original field.options list
	 * with additional default options
	 */
	private filterOptions(field: any): void {
		field.filteredOptions = field.options || [];

		field.filteredOptions = field.filteredOptions.filter((option: any) => option != null);

		if (field.type_normalized === 'LIST' || field.type_normalized === 'MULTI_LIST') {
			field.filteredOptions = [{ name: '', value: '' }, ...field.filteredOptions];
		}
	}

	// NOTE: Originally located in srsField.directive.js
	// >> Dependent select fields
	private isEmpty(formItem: any, formContent: any): boolean {
		if (formItem.hidden) {
			return true;
		}
		if (formItem.type_normalized == "STRING") {
			return !formItem.list || formItem.list.length < 1 || !formItem.list[0].content;
		} else {
			return !formContent;
		}
	}

	/**
	 * Applies filtering logic based on the parentMask value.
	 * Example: "parentMask" : "en OR eng" 
	 */
	private applyFilter(formItem: any) {
		if (!formItem?.options) {
			formItem.filteredOptions = [];
			return;
		}

		if (formItem.filter) {
			let newOptions = formItem.options.filter((option: any) =>
				this.filterComparator(option.parentMask, formItem.filter?.parentMask)
			);
			formItem.filteredOptions = [{ name: '', value: '' }, ...newOptions];
			formItem.content = '';
		} else {
			// Show all options if no filter
			formItem.filteredOptions = [{ name: '', value: '' }, ...formItem.options];
			formItem.content = '';
		}
	}

	/**
	 * Custom filter function to compare values.
	 */
	private filterComparator(actual: string, expected: string): boolean {
		if (!actual || !expected) return false;

		let parts = actual.split(" OR ");
		return parts.some((part: string) => part.toLowerCase() === expected.toLowerCase());
	}

	/**
	 * Handles field updates and adjusts dependents accordingly.
	 */
	onUpdateFn(formItem: any, ignoreDependents: boolean = false, formValue: any = null) {
		if (!formItem || !formItem.dependents || ignoreDependents) return;
		
		let content = formValue || formItem.content || null;

		let validDeps: { [key: string]: any } = {};

		if (formItem.options) {
			for (let option of formItem.options) {
				// checks if content is array or just one object which is the difference between list multi and list
				if (option && (option.value === content ||
					(Array.isArray(content)
						&& content.find((c: any) => {
							return c.value === option.value
						}))
				)) {
					for (const child of option.childs) {
						if (child && child.name) {
							validDeps[child.name] = child;
						}
					}
				}
			}
		}

		Object.values(formItem.dependents).forEach((dependent: any) => {
			if (dependent) {
				if (!validDeps[dependent.name] || this.isEmpty(formItem, content)) {
					dependent.searchField.filter = null;
					//dependent.searchField.availableOptions = this.filterOptions(dependent.searchField);
					this.onUpdateFn(dependent.searchField, ignoreDependents, formValue);
				}
				else {
					dependent.searchField.hidden = false;
					dependent.searchField.filter = content != null ? { parentMask: content } : null;
					if (dependent.searchField.type_normalized == "RADIO" ||
						dependent.searchField.type_normalized == "LIST" ||
						dependent.searchField.type_normalized == "LIST_MULTI"
					) {
						if (!dependent.searchField.content) {
							dependent.searchField.content = dependent.searchField.options[0] ? dependent.searchField.options[0].value : null;
						}
						//dependent.searchField.availableOptions = this.filterOptions(dependent.searchField);
						this.onUpdateFn(dependent.searchField, ignoreDependents, formValue);
					}
				}
				this.applyFilter(dependent.searchField);
			}
		});
	}
	// << END Dependent select fields		
}
